import React, { useMemo, useState } from "react";
import {
  WalletAction,
  WalletLocation,
  WalletProvider,
  ACTION_PRESETS,
  PROVIDER_LIMITS,
  APPLE_FEATURED_ACTIONS_NOTE,
  isActionActive,
} from "../../api/walletActions";

// Admin/demo editor for a pass's wallet ACTIONS (front/action links) and
// LOCATIONS. Emits the provider-neutral arrays the backend persists onto the
// pass record (walletActions / walletLocations). Designed to demonstrate:
//   • Apple pass with two front/action links (feature-flagged) where available
//   • Google pass with multiple active links
//   • Google pass with an expanded location list (up to the verified API cap)
//   • create-time AND update-time action changes (same payload on both paths)

interface Props {
  initialActions?: WalletAction[];
  initialLocations?: WalletLocation[];
  /** Called whenever the config changes, with the payload the backend expects. */
  onChange?: (payload: { walletActions: WalletAction[]; walletLocations: WalletLocation[] }) => void;
}

let idSeq = 0;
const nextId = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${idSeq++}`;

const PROVIDERS: WalletProvider[] = ["both", "google", "apple"];

const inputCls =
  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm";

const WalletActionsConfig: React.FC<Props> = ({ initialActions, initialLocations, onChange }) => {
  const [actions, setActions] = useState<WalletAction[]>(initialActions ?? []);
  const [locations, setLocations] = useState<WalletLocation[]>(initialLocations ?? []);

  const emit = (a: WalletAction[], l: WalletLocation[]) =>
    onChange?.({ walletActions: a, walletLocations: l });

  const updateActions = (next: WalletAction[]) => {
    setActions(next);
    emit(next, locations);
  };
  const updateLocations = (next: WalletLocation[]) => {
    setLocations(next);
    emit(actions, next);
  };

  const addAction = () => {
    const preset = ACTION_PRESETS[Math.min(actions.length, ACTION_PRESETS.length - 1)];
    updateActions([
      ...actions,
      {
        id: nextId("action"),
        provider: "both",
        label: preset.label,
        url: "",
        actionType: preset.actionType,
        priority: 0,
        enabled: true,
      },
    ]);
  };

  const addLocation = () => {
    updateLocations([
      ...locations,
      {
        id: nextId("loc"),
        name: "",
        latitude: NaN as unknown as number,
        longitude: NaN as unknown as number,
        provider: "both",
        enabled: true,
      },
    ]);
  };

  const patchAction = (id: string, patch: Partial<WalletAction>) =>
    updateActions(actions.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const patchLocation = (id: string, patch: Partial<WalletLocation>) =>
    updateLocations(locations.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const removeAction = (id: string) => updateActions(actions.filter((a) => a.id !== id));
  const removeLocation = (id: string) => updateLocations(locations.filter((l) => l.id !== id));

  // Live preview counts of what each provider will actually render.
  const preview = useMemo(() => {
    const now = Date.now();
    const active = actions.filter((a) => isActionActive(a, now));
    const googleLinks = active.filter((a) => a.provider !== "apple");
    const appleLinks = active.filter((a) => a.provider !== "google");
    const googleLocs = locations.filter(
      (l) => l.enabled && l.provider !== "apple" && Number.isFinite(l.latitude) && Number.isFinite(l.longitude),
    );
    const appleLocs = locations.filter(
      (l) => l.enabled && l.provider !== "google" && Number.isFinite(l.latitude) && Number.isFinite(l.longitude),
    );
    return {
      googleLinks: Math.min(googleLinks.length, PROVIDER_LIMITS.google.maxLinks),
      appleFeatured: Math.min(appleLinks.length, PROVIDER_LIMITS.apple.maxFeaturedActions),
      appleBackLinks: appleLinks.length,
      googleLocs: Math.min(googleLocs.length, PROVIDER_LIMITS.google.maxLocations),
      appleLocs: Math.min(appleLocs.length, PROVIDER_LIMITS.apple.maxLocations),
    };
  }, [actions, locations]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Wallet Actions &amp; Locations</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configurable front/action links and store locations included when a pass is created and
          updated later via push.
        </p>
      </div>

      {/* Apple availability note — required internal/demo copy. */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
        <span className="font-semibold">Apple availability:</span> {APPLE_FEATURED_ACTIONS_NOTE} Until
        then, Apple actions appear as tappable back-of-pass links. Google links are live now and support
        more links than Apple.
      </div>

      {/* ─────────── Actions ─────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Action links</h3>
          <button
            onClick={addAction}
            className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
          >
            + Add action
          </button>
        </div>

        {actions.length === 0 && (
          <p className="text-sm text-gray-400 italic">No actions yet — add one to surface links on the pass.</p>
        )}

        {actions.map((a) => (
          <div key={a.id} className="border border-gray-200 rounded-md p-3 grid grid-cols-12 gap-2 items-start">
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select
                className={inputCls}
                value={a.actionType ?? ""}
                onChange={(e) => {
                  const preset = ACTION_PRESETS.find((p) => p.actionType === e.target.value);
                  patchAction(a.id, { actionType: e.target.value, label: preset?.label ?? a.label });
                }}
              >
                {ACTION_PRESETS.map((p) => (
                  <option key={p.actionType} value={p.actionType}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Label</label>
              <input className={inputCls} value={a.label} onChange={(e) => patchAction(a.id, { label: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1">URL</label>
              <input
                className={inputCls}
                placeholder="https://…"
                value={a.url}
                onChange={(e) => patchAction(a.id, { url: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Provider</label>
              <select
                className={inputCls}
                value={a.provider}
                onChange={(e) => patchAction(a.id, { provider: e.target.value as WalletProvider })}
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Starts (optional)</label>
              <input
                type="datetime-local"
                className={inputCls}
                value={a.startsAt ?? ""}
                onChange={(e) => patchAction(a.id, { startsAt: e.target.value || undefined })}
              />
            </div>
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Ends (optional)</label>
              <input
                type="datetime-local"
                className={inputCls}
                value={a.endsAt ?? ""}
                onChange={(e) => patchAction(a.id, { endsAt: e.target.value || undefined })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Priority</label>
              <input
                type="number"
                className={inputCls}
                value={a.priority ?? 0}
                onChange={(e) => patchAction(a.id, { priority: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-6">
              <input
                id={`en_${a.id}`}
                type="checkbox"
                checked={a.enabled}
                onChange={(e) => patchAction(a.id, { enabled: e.target.checked })}
              />
              <label htmlFor={`en_${a.id}`} className="text-sm text-gray-600">
                Enabled
              </label>
            </div>
            <div className="col-span-2 flex justify-end pt-5">
              <button onClick={() => removeAction(a.id)} className="text-red-500 text-sm hover:underline">
                Remove
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* ─────────── Locations ─────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Locations</h3>
          <button
            onClick={addLocation}
            className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
          >
            + Add location
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Google caps geofenced locations at {PROVIDER_LIMITS.google.maxLocations} (current public API limit);
          Apple at {PROVIDER_LIMITS.apple.maxLocations}. Extra locations beyond the cap are dropped at issue time.
        </p>

        {locations.map((l) => (
          <div key={l.id} className="border border-gray-200 rounded-md p-3 grid grid-cols-12 gap-2 items-start">
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input className={inputCls} value={l.name} onChange={(e) => patchLocation(l.id, { name: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Latitude</label>
              <input
                type="number"
                className={inputCls}
                value={Number.isFinite(l.latitude) ? l.latitude : ""}
                onChange={(e) => patchLocation(l.id, { latitude: parseFloat(e.target.value) })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Longitude</label>
              <input
                type="number"
                className={inputCls}
                value={Number.isFinite(l.longitude) ? l.longitude : ""}
                onChange={(e) => patchLocation(l.id, { longitude: parseFloat(e.target.value) })}
              />
            </div>
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Address (optional)</label>
              <input className={inputCls} value={l.address ?? ""} onChange={(e) => patchLocation(l.id, { address: e.target.value || undefined })} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Provider</label>
              <select
                className={inputCls}
                value={l.provider}
                onChange={(e) => patchLocation(l.id, { provider: e.target.value as WalletProvider })}
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-9 flex items-center gap-2">
              <input
                id={`len_${l.id}`}
                type="checkbox"
                checked={l.enabled}
                onChange={(e) => patchLocation(l.id, { enabled: e.target.checked })}
              />
              <label htmlFor={`len_${l.id}`} className="text-sm text-gray-600">
                Enabled
              </label>
            </div>
            <div className="col-span-3 flex justify-end">
              <button onClick={() => removeLocation(l.id)} className="text-red-500 text-sm hover:underline">
                Remove
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* ─────────── Live preview ─────────── */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
        <span className="font-semibold">Will render:</span>{" "}
        Google {preview.googleLinks} link(s), {preview.googleLocs} location(s) · Apple{" "}
        {preview.appleBackLinks} back-of-pass link(s) ({preview.appleFeatured} as Featured Actions when
        the flag is on), {preview.appleLocs} location(s)
      </div>
    </div>
  );
};

export default WalletActionsConfig;
